import * as React from 'react';

import type {
  DescriptionPage_Domain as DescriptionPageDomain,
  DescriptionPage_Instructor as DescriptionPageInstructor,
  DescriptionPage_Partner as DescriptionPagePartner,
} from '__generated__/graphql-types-do-not-use';

import {
  KEYWORD_IDS,
  getInstructorMarkup,
  getSchemaMarkupElement,
  getSchemaOrgBooleanString,
  getTimeRequiredString,
  getTrimmedDescription,
} from 'bundles/seo/utils/schemaMarkupUtils';
import { clearEmptyFields } from 'bundles/seo/utils/utils';
import { getBreadcrumbSchema } from 'bundles/unified-description-page-common/utils/schemaMarkupUtils';

type Props = {
  slug: string;
  name: string;
  description?: string | null;
  primaryLanguages?: Array<string> | null;
  timeRequired?: number | null;
  partners?: Array<DescriptionPagePartner> | null;
  domain?: DescriptionPageDomain;
  ratings?: {
    averageFiveStarRating?: number | null;
    commentCount?: number | null;
    ratingCount?: number | null;
  } | null;
  instructors?: Array<DescriptionPageInstructor> | null;
};

const DescriptionPageProjectPageSchemaMarkup = ({
  slug,
  name,
  description,
  primaryLanguages,
  timeRequired,
  partners,
  domain,
  ratings,
  instructors,
}: Props) => {
  const keywordId = KEYWORD_IDS.PROJECT;
  const breadcrumbType = getBreadcrumbSchema(domain, keywordId);

  // Projects do not have an institutional provider - use instructor instead
  const provider = instructors && instructors.length > 0 ? getInstructorMarkup(instructors) : undefined;

  const projectType = {
    '@id': `https://www.coursera.org/projects/${slug}#${keywordId}`,
    '@type': 'Course',
    name,
    url: `https://www.coursera.org/projects/${slug}`,
    description: description ? getTrimmedDescription(description, 160) : '',
    image: partners?.[0]?.productBrandingLogo,
    educationalCredentialAwarded: 'Coursera Certificate',
    isAccessibleForFree: getSchemaOrgBooleanString(false),
    inLanguage: primaryLanguages,
    ...(timeRequired
      ? {
          timeRequired: getTimeRequiredString({ hours: timeRequired }),
        }
      : {}),
    ...(ratings
      ? {
          aggregateRating: clearEmptyFields({
            '@type': 'AggregateRating',
            ratingValue: ratings.averageFiveStarRating,
            ratingCount: ratings.ratingCount,
            reviewCount: ratings.commentCount,
          }),
        }
      : {}),
    provider,
  };

  const graph = [breadcrumbType, projectType];

  return <div>{getSchemaMarkupElement(graph)}</div>;
};

export default DescriptionPageProjectPageSchemaMarkup;
